import { mapGetters } from 'vuex';

export default {
  computed: {
    ...mapGetters('bookmarks', [
      'activeBookmarks',
    ]),
    ...mapGetters('categories', [
      'categories',
    ]),
    ...mapGetters('flashcardStates', [
      'flashcardStateList',
    ]),
    ...mapGetters('categoryTypes', [
      'multipleChoiceCategoryTypes',
      'mnemonicCategoryTypes',
      'terminologyCategoryTypes',
      'attachmentCategoryTypes',
      'quizCategoryTypes',
      'testSimulationCategoryTypes',
      'userQuizCategoryType',
    ]),
    ...mapGetters('categoryStatuses', [
      'notStartedStatus',
      'completedStatus',
      'inProgressStatus',
    ]),
    resourceCategories() {
      const isQuizBuilder = ['StepOne', 'quiz-builder'].includes(this.$options._componentTag) || this.$route.name === 'quizzes#create';
      const isQuizRoute = this.$route.name.includes('quizzes');

      let categories = [...this.$store.getters['categories/all']].sort((a, b) => a.number - b.number);
      if (this.$route.name === 'categoryTypes#index' && this.$route.params.categoryTypeId) {
        categories = categories.filter(c => c.categoryTypeId === parseInt(this.$route.params.categoryTypeId));
      } else if (this.$route.name === 'scenarios#index' && this.$route.params.categoryTypeId) {
        categories = categories.filter(c => c.categoryTypeId === parseInt(this.$route.params.categoryTypeId));
      } else if (isQuizRoute && !isQuizBuilder) {
        categories = categories.filter(c => c.categoryTypeId === this.userQuizCategoryType.id);
      } else if (this.currentResource.destination === 'custom_quiz_grouping') {
        const grouping = this.$store.getters['groupings/all'][this.currentResource.resourceId];
        categories = this.filterByGrouping(categories, grouping);
      } else if (this.currentResource.resourceId && this.currentResource.resourceType === 'CategoryType') {
        categories = categories.filter(c => c.categoryTypeId === this.currentResource.resourceId);
      } else if (this.currentResource.destination === 'test_simulations') {
        const ids = this.$store.getters['categoryTypes/testSimulationCategoryTypes'].map(t => t.id);
        categories = categories.filter(c => ids.includes(c.categoryTypeId));
      } else if (this.$route.params.categoryId) {
        const parent = this.$store.getters['categories/categories'][this.$route.params.categoryId];
        const ids = [parent.id, ...parent.children];
        categories = categories.filter(c => ids.includes(c.id));
      } else if (this.$route.query.categoryTypeId) {
        categories = categories.filter(c => c.categoryTypeId === parseInt(this.$route.query.categoryTypeId));
      } else if (this.$route.query.groupingId) {
        const grouping = this.$store.getters['groupings/all'][this.$route.query.groupingId];
        categories = this.filterByGrouping(categories, grouping);
      } else {
        let items = this.$store.getters['navigationItems/navigationItemsList'].filter(ni => {
          // if no resource is present (only applies to progress nav item and custom quiz nav item), find all flashcard nav items the user can access
          // and that aren't onboarding/questionnaire categories and combine categories
          if (ni.destination === 'flashcard_categories' && !ni.dynamic && this.$access(this.$store, 'navigationItem', ni.id)) {
            return true;
            // this logic shouldn't be needed, but keeping it here just in case
            // const multipleChoiceTypes = this.$store.getters['categoryTypes/multipleChoiceCategoryTypes'];
            // const type = multipleChoiceTypes.find(ct => ct.id === ni.resourceId);
            // if (type) {
            //   return !!type.name.match(/multiple-choice(\d|-|)*$/);
            // }
          }
        });
        let ids = items.map(ni => ni.resourceId);
        categories = categories.filter(c => ids.includes(c.categoryTypeId));
      }

      return categories;
    },
    unlockedResourceCategories() {
      const result = [];
      const parentCategories = this.resourceCategories.filter(c => !c.parentCategoryId);

      for (const parentCategory of parentCategories) {
        const childCategories = this.resourceCategories.filter(c => c.parentCategoryId === parentCategory.id);

        // Parent category with no children
        if (childCategories.length === 0 && this.$access(this.$store, 'category', parentCategory.id)) {
          result.push(parentCategory);
        }

        // Parent category with children
        else if (childCategories.length > 0) {
          const unlockedChildCategories = childCategories.filter(c => this.$access(this.$store, 'category', c.id));
          if (unlockedChildCategories.length > 0) {
            result.push(parentCategory);
            result.push(...unlockedChildCategories);
          }
        }
      }

      return result;
    },
    scopedCategories() {
      const categoryId = this.$route.params.categoryId;
      if (!categoryId) {
        return this.resourceCategories.filter(c => !c.parentCategoryId);
      }
      return this.resourceCategories.filter(c => c.parentCategoryId == categoryId);
    },
    flashcardCounts() {
      return this.getFlashcardCounts(this.resourceCategories);
    },
    unlockedFlashcardCounts() {
      return this.getFlashcardCounts(this.unlockedResourceCategories, true);
    },
  },
  methods: {
    filterByGrouping(categories, grouping) {
      const categoryTypeIds = grouping.assetAssociations
        .filter(aa => aa.assetBType === 'CategoryType')
        .map(aa => aa.assetBId);
      return categories.filter(c => categoryTypeIds.includes(c.categoryTypeId));
    },
    quizFlashcardStates(category) {
      return this.flashcardStateList.filter(fs => fs.categoryId == category.id);
    },
    getFlashcardCounts(categories, checkAccess = false) {
      const totals = {};
      const greenFlashcardStatus = this.$store.getters['flashcardStatuses/greenFlashcardStatus'];
      const redFlashcardStatus = this.$store.getters['flashcardStatuses/redFlashcardStatus'];
      const yellowFlashcardStatus = this.$store.getters['flashcardStatuses/yellowFlashcardStatus'];

      for (let category of categories) {
        let data = {};

        let ids = [category.id, ...category.descendants];
        let leafs = this.$store.getters['categories/all']
          .filter(c => ids.includes(c.id) && c.children.length < 1)
          .filter(c => !checkAccess || this.$access(this.$store, 'category', c.id));

        let states = this.flashcardStateList.filter(s => leafs.map(c => c.id).includes(s.categoryId));
        let bookmarks = this.activeBookmarks.filter(b => leafs.map(c => c.id).includes(b.contextId) && !b.deleted);

        data['count'] = leafs.map(c => c.counts.total).reduce((a, b) => a + b, 0);
        data['green'] = states.filter(s => s.flashcardStatusId == greenFlashcardStatus.id).length;
        data['yellow'] = states.filter(s => s.flashcardStatusId == yellowFlashcardStatus.id).length;
        data['red'] = states.filter(s => s.flashcardStatusId == redFlashcardStatus.id).length;
        data['isAnswered'] = states.filter(s => (s.answerIds && s.answerIds.length > 0) || (s.rawAnswerData && Object.keys(s.rawAnswerData).length > 0)).length;
        data['bookmarked'] = bookmarks.length;
        data['bookmarkedAndGreen'] = 0;
        data['bookmarkedAndYellow'] = 0;
        data['bookmarkedAndRed'] = 0;
        if (bookmarks.length > 0) {
          bookmarks
            .forEach(bookmark => {
              const flashcardId = bookmark.bookmarkableId;
              const flashcardState = states.find(s => s.flashcardId === flashcardId);
              if (flashcardState?.flashcardStatusId === greenFlashcardStatus.id) {
                data['bookmarkedAndGreen'] = (data['bookmarkedAndGreen']) + 1;
              } else if (flashcardState?.flashcardStatusId === redFlashcardStatus.id) {
                data['bookmarkedAndRed'] = (data['bookmarkedAndRed']) + 1;
              } else if (flashcardState?.flashcardStatusId === yellowFlashcardStatus.id) {
                data['bookmarkedAndYellow'] = (data['bookmarkedAndYellow']) + 1;
              }
            });
        }
        data['correct'] = states.filter(s => s.correct && [greenFlashcardStatus.id, redFlashcardStatus.id, yellowFlashcardStatus.id].includes(s.flashcardStatusId)).length;
        data['answered'] = data['green'] + data['yellow'] + data['red'];
        data['unanswered'] = data['count'] - data['answered'];

        data['all'] = data['count'];
        data['any'] = data['answered'];

        totals[category.id] = data;
      }

      let all = {};
      let allCategories = categories.filter(c => c.parentCategoryId == null);
      let counts = allCategories.map(c => totals[c.id]);

      all['count'] = counts.map(c => c['count']).reduce((a, b) => a + b, 0);
      all['green'] = counts.map(c => c['green']).reduce((a, b) => a + b, 0);
      all['yellow'] = counts.map(c => c['yellow']).reduce((a, b) => a + b, 0);
      all['red'] = counts.map(c => c['red']).reduce((a, b) => a + b, 0);
      all['bookmarked'] = counts.map(c => c['bookmarked']).reduce((a, b) => a + b, 0);
      all['correct'] = counts.map(c => c['correct']).reduce((a, b) => a + b, 0);
      all['answered'] = counts.map(c => c['answered']).reduce((a, b) => a + b, 0);
      all['unanswered'] = counts.map(c => c['unanswered']).reduce((a, b) => a + b, 0);

      all['all'] = all['count'];
      all['any'] = all['answered'];

      totals['all'] = all;
      return totals;
    },
    /**
     * @method filteredFlashcardCount
     * @param {Object} categoryData - The category data object containing counts.
     * @param {Array} filterKeys - The array of filter keys to apply.
     * @returns {Number} - The filtered flashcard counts based on the provided filter keys.
     * @description This method calculates the filtered flashcard counts based on the provided filter keys.
     * It takes into account the counts for different categories such as 'bookmarked', 'green', 'yellow', 'red', 'unanswered', and 'all'.
     */
    filteredFlashcardCount(categoryData, filterKeys) {
      if (!filterKeys) {
        filterKeys = [];
      }

      if (filterKeys?.includes('all')) {
        return categoryData.count;
      }

      if (!filterKeys.includes('bookmarked')) {
        return filterKeys.reduce((total, key) => {
          if (categoryData[key]) {
            total += categoryData[key];
          }
          return total;
        }, 0);
      } else if (filterKeys.includes('bookmarked')) {
        let count = filterKeys.reduce((total, key) => {
          if (categoryData[key]) {
            total += categoryData[key];
          }
          return total;
        }, 0);

        const bookmarkedAndAnswered =
        categoryData.bookmarkedAndRed +
        categoryData.bookmarkedAndYellow +
        categoryData.bookmarkedAndGreen;

        const bookmarkedAndUnanswered = categoryData.bookmarked - bookmarkedAndAnswered;

        if (filterKeys.includes('unanswered')) {
          count -= bookmarkedAndUnanswered;
        }

        if (filterKeys.includes('red')) {
          count -= categoryData.bookmarkedAndRed;
        }

        if (filterKeys.includes('yellow')) {
          count -= categoryData.bookmarkedAndYellow;
        }
        if (filterKeys.includes('green')) {
          count -= categoryData.bookmarkedAndGreen;
        }
        return count;
      }

      return 0;
    },
  },
};
